import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import "./NavBar.css";

function NavBar({
  decades,
  activeDecade,
  onDecadeClick,
  isTimeline,
  onMenuHiddenChange,
  language = "EN",
  selectedLanguage = "EN",
  languageSwitchPhase = "",
  onLanguageChange,
}) {
  const location = useLocation();
  const isHeroPage = location.pathname === "/";
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isMenuHidden, setIsMenuHidden] = useState(false);
  const [isScrolled, setIsScrolled] = useState(window.scrollY > 8);
  const navbarRef = useRef(null);
  const lastScrollYRef = useRef(window.scrollY);
  const scrollFrameRef = useRef(null);
  const scrollDirectionRef = useRef(null);
  const directionTravelRef = useRef(0);

  // Keep the real header height available to the page layout. The navbar is
  // sticky (and therefore remains in document flow), while this variable lets
  // existing page spacing stay visually unchanged as the menu compacts.
  useEffect(() => {
    const navbar = navbarRef.current;
    if (!navbar) return undefined;
    let heightSettleTimer = null;

    const updateNavbarHeight = () => {
      // Keep the expanded header height during the compact animation. Updating
      // this root variable on every font-size frame would relayout long pages.
      if (navbar.classList.contains("compact")) return;
      const height = navbar.getBoundingClientRect().height;
      document.documentElement.style.setProperty(
        "--navbar-height",
        `${height}px`
      );
    };

    const scheduleNavbarHeightUpdate = () => {
      if (heightSettleTimer) window.clearTimeout(heightSettleTimer);
      heightSettleTimer = window.setTimeout(updateNavbarHeight, 350);
    };

    updateNavbarHeight();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleNavbarHeightUpdate)
        : null;
    resizeObserver?.observe(navbar);
    window.addEventListener("resize", scheduleNavbarHeightUpdate);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleNavbarHeightUpdate);
      if (heightSettleTimer) window.clearTimeout(heightSettleTimer);
    };
  }, []);

  // 检测是否为移动设备
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // One animation-frame update per paint. Keeping the previous scroll value
  // in a ref avoids rerendering and rebinding listeners on every scroll event.
  useEffect(() => {
    const updateScrollState = () => {
      scrollFrameRef.current = null;
      const currentScrollY = window.scrollY;
      const previousScrollY = lastScrollYRef.current;
      const deltaY = currentScrollY - previousScrollY;
      const direction = deltaY > 0 ? "down" : deltaY < 0 ? "up" : null;

      if (direction && direction !== scrollDirectionRef.current) {
        scrollDirectionRef.current = direction;
        directionTravelRef.current = 0;
      }
      directionTravelRef.current += Math.abs(deltaY);

      if (
        direction === "down" &&
        currentScrollY > 150 &&
        directionTravelRef.current >= 48
      ) {
        setIsMenuHidden((hidden) => (hidden ? hidden : true));
      } else if (
        (direction === "up" && directionTravelRef.current >= 36) ||
        currentScrollY <= 80
      ) {
        setIsMenuHidden((hidden) => (hidden ? false : hidden));
      }

      const nextScrolled = currentScrollY > 8;
      setIsScrolled((scrolled) =>
        scrolled === nextScrolled ? scrolled : nextScrolled
      );
      lastScrollYRef.current = currentScrollY;
    };

    const handleScroll = () => {
      if (scrollFrameRef.current !== null) return;
      scrollFrameRef.current = window.requestAnimationFrame(updateScrollState);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
    };
  }, []);

  // 通知父组件菜单栏状态变化
  useEffect(() => {
    if (typeof onMenuHiddenChange === "function") {
      onMenuHiddenChange(isMenuHidden);
    }
  }, [isMenuHidden, onMenuHiddenChange]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleLanguageSwitch = (code) => {
    if (typeof onLanguageChange === "function") {
      onLanguageChange(code);
    }
  };

  return (
    <header
      ref={navbarRef}
      className={`navbar${!isMobile && isMenuHidden ? " compact" : ""}${
        isScrolled ? " scrolled" : ""
      }${isMobile && menuOpen ? " menu-open" : ""}`}
    >
      <div className="navbar-primary-layer">
        {/* 第一行：左侧logo + 右侧按钮/搜索 */}
        <div className="top-row">
          <Link to="/" className="site-logo-link">
            <div className="site-logo">
              <span className="logo-black">ARCHIVE</span>
              <span className="logo-small"> OF </span>
              <span className="logo-grey"> LIGHT</span>
            </div>
          </Link>
          <div className="navbar-right">
            {isMobile && (
              <>
                <div
                  className="mobile-language-switch"
                  role="group"
                  aria-label="Language"
                >
                  <button
                    type="button"
                    className={`mobile-lang-btn ${
                      selectedLanguage === "EN" ? "active" : ""
                    }`}
                    aria-label="Switch to English"
                    aria-pressed={selectedLanguage === "EN"}
                    onClick={() => handleLanguageSwitch("EN")}
                  >
                    EN
                  </button>
                  <span className="mobile-lang-separator" aria-hidden="true">
                    /
                  </span>
                  <button
                    type="button"
                    className={`mobile-lang-btn mobile-lang-btn-cn ${
                      selectedLanguage === "CN" ? "active" : ""
                    }`}
                    lang="zh-Hans"
                    aria-label="切换至简体中文"
                    aria-pressed={selectedLanguage === "CN"}
                    onClick={() => handleLanguageSwitch("CN")}
                  >
                    中
                  </button>
                </div>
                <button
                  className="hamburger-menu"
                  onClick={toggleMenu}
                  aria-label="Toggle navigation menu"
                >
                  <span
                    className={`hamburger-line ${menuOpen ? "open" : ""}`}
                  ></span>
                  <span
                    className={`hamburger-line ${menuOpen ? "open" : ""}`}
                  ></span>
                  <span
                    className={`hamburger-line ${menuOpen ? "open" : ""}`}
                  ></span>
                </button>
              </>
            )}
          </div>
        </div>
        <div
          className={
            "line-separator" +
            (!isMobile && isMenuHidden ? " hide-separator" : "")
          }
        ></div>
      </div>
      {/* 第二行：主菜单 - 分开渲染移动端和桌面端菜单 */}
      {isMobile ? (
        <div className={`bottom-row mobile${menuOpen ? " open" : ""}`}>
          <nav
            className={`nav-menu${menuOpen ? " open" : ""}${
              languageSwitchPhase ? ` lang-${languageSwitchPhase}` : ""
            }`}
          >
            <Link to="/timeline" onClick={() => setMenuOpen(false)}>
              {language === "CN" ? "时间线" : "TIMELINE"}
            </Link>
            <Link to="/photographers" onClick={() => setMenuOpen(false)}>
              {language === "CN" ? "摄影师" : "PHOTOGRAPHERS"}
            </Link>
            <Link to="/genres" onClick={() => setMenuOpen(false)}>
              {language === "CN" ? "摄影风格" : "GENRE"}
            </Link>
            <Link to="/about" onClick={() => setMenuOpen(false)}>
              {language === "CN" ? "关于本站" : "ABOUT"}
            </Link>
          </nav>
        </div>
      ) : !isHeroPage ? (
        <div
          className={`desktop-menu-layer${
            isMenuHidden ? " hide-menu" : ""
          }`}
        >
          <div className="bottom-row">
            <nav
              className={`nav-menu${
                languageSwitchPhase ? ` lang-${languageSwitchPhase}` : ""
              }`}
            >
              <NavLink to="/timeline">
                {language === "CN" ? "时间线" : "TIMELINE"}
              </NavLink>
              <NavLink to="/photographers">
                {language === "CN" ? "摄影师" : "PHOTOGRAPHERS"}
              </NavLink>
              <NavLink to="/genres">
                {language === "CN" ? "摄影风格" : "GENRE"}
              </NavLink>
              <NavLink to="/about">
                {language === "CN" ? "关于本站" : "ABOUT"}
              </NavLink>
            </nav>
          </div>
          <div className="line-separator"></div>
        </div>
      ) : null}
      {/* 不在这里渲染 DecadeNavBar，已在 AppContent 统一渲染 */}
    </header>
  );
}

export default NavBar;
